import React, { useState, useEffect } from 'react';
import { Box, Container, CircularProgress } from '@mui/material';
import ModuleListTable from './ModuleListTable';
import ModuleForm from './ModuleForm';
import { AnyModule } from './module.types';
import { useLocation } from 'react-router-dom';
import { fetchModules } from './module.api';
import { useAccessToken } from '@features/user';
import { ModuleListParams } from './module.types';

export const AdminModulesPage: React.FC = () => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const userId = searchParams.get('userId');
  const moduleId = searchParams.get('moduleId');
  const moduleType = searchParams.get('moduleType') as 'public' | 'user' | null;

  const { getAccessToken } = useAccessToken();
  const [loading, setLoading] = useState<boolean>(false);

  // State to track the current view (list or form)
  const [view, setView] = useState<'list' | 'create' | 'edit'>('list');
  // State to store the module being edited (if any)
  const [currentModule, setCurrentModule] = useState<AnyModule | undefined>();

  // Load a specific module if moduleId is provided in URL
  useEffect(() => {
    const loadModuleFromUrl = async () => {
      if (moduleId && moduleType) {
        setLoading(true);
        try {
          const accessToken = await getAccessToken();
          if (!accessToken) {
            console.error('No access token available');
            setLoading(false);
            return;
          }

          // Use filterByType and moduleId to find the specific module
          const filters: ModuleListParams = {
            filterByType: moduleType,
            filterByModuleId: moduleId, // Using search to find by moduleId
          };

          // Add userId filter if we're looking at a user module
          if (moduleType === 'user' && userId) {
            filters.filterByUserId = userId;
          }

          const response = await fetchModules(filters, accessToken);

          // Find the module with matching ID and version
          const foundModule = response.data.find(m => m.module_id === moduleId);

          if (foundModule) {
            setCurrentModule(foundModule);
            setView('edit');
          }
        } catch (error) {
          console.error('Failed to load module:', error);
        } finally {
          setLoading(false);
        }
      }
    };

    loadModuleFromUrl();
  }, [moduleId, moduleType, userId]);

  // Handler for creating a new module
  const handleCreateModule = () => {
    setCurrentModule(undefined);
    setView('create');
  };

  // Handler for editing an existing module
  const handleEditModule = (module: AnyModule) => {
    setCurrentModule(module);
    setView('edit');
  };

  // Handler for canceling form actions
  const handleCancelForm = () => {
    setView('list');
  };

  // Handler for successful save
  const handleSaveSuccess = () => {
    setView('list');
  };

  if (loading) {
    return (
      <Container maxWidth={"xl"}>
        <Box sx={{ py: 4, display: "flex", justifyContent: "center" }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth={"xl"}>
      <Box sx={{ py: 4 }}>
        {view === 'list' && (
          <ModuleListTable
            onCreateModule={handleCreateModule}
            onEditModule={handleEditModule}
            userId={userId || undefined}
          />
        )}

        {(view === 'create' || view === 'edit') && (
          <ModuleForm
            module={view === 'edit' ? currentModule : undefined}
            onCancel={handleCancelForm}
            onSave={handleSaveSuccess}
            isCreating={view === 'create'}
          />
        )}
      </Box>
    </Container>
  );
};

export default AdminModulesPage;
